import { ICFS } from '@/models/cfs';
import { IKase, IPenalCode } from '@/models/kase';
import { ITask } from '@/models/tasks';

import { IAgency } from './agency';
import { IAuditModel } from './base';
import { IFile } from './file';
import { IMasterNamePerson } from './master.names';
import { IUser } from './user';
import { IVendor } from './vendors';

export enum ProcessType {
  WARRANT = 'WARRANT',
  EXTRADITION = 'EXTRADITION',
  PROTECTIVE_ORDER = 'PROTECTIVE_ORDER',
  ARREST_WARRANT = 'ARREST_WARRANT',
  BENCH_WARRANT = 'BENCH_WARRANT',
}

export const ProcessTypeFriendlyNames: Record<ProcessType, string> = {
  [ProcessType.WARRANT]: 'Warrant',
  [ProcessType.EXTRADITION]: 'Extradition',
  [ProcessType.PROTECTIVE_ORDER]: 'Protective Order',
  [ProcessType.ARREST_WARRANT]: 'Arrest Warrant',
  [ProcessType.BENCH_WARRANT]: 'Bench Warrant',
};

export enum CrimeClass {
  FELONY = 'FELONY',
  MISDEMEANOR = 'MISDEMEANOR',
}

export const CrimeClassFriendlyNames: Record<CrimeClass, string> = {
  [CrimeClass.FELONY]: 'Felony',
  [CrimeClass.MISDEMEANOR]: 'Misdemeanor',
};

export enum Disposition {
  ACTIVE_OR_WANTED = 'ACTIVE_OR_WANTED',
  ACTIVE_LOCALLY_EXTRADITE = 'ACTIVE_LOCALLY_EXTRADITE',
  ACTIVE_PROTECTIVE_ORDER = 'ACTIVE_PROTECTIVE_ORDER',
  INACTIVE = 'INACTIVE',
  RECALLED_RETURNED_TO_COURT = 'RECALLED_RETURNED_TO_COURT',
  SERVED_NOT_YET_RETURNED = 'SERVED_NOT_YET_RETURNED',
  SERVED_RETURNED_TO_COURT = 'SERVED_RETURNED_TO_COURT',
}

export const DispositionFriendlyNames: Record<Disposition, string> = {
  [Disposition.ACTIVE_OR_WANTED]: 'Active or Wanted',
  [Disposition.ACTIVE_LOCALLY_EXTRADITE]: 'Active Locally Extradite',
  [Disposition.ACTIVE_PROTECTIVE_ORDER]: 'Active Protective Order',
  [Disposition.INACTIVE]: 'Inactive',
  [Disposition.RECALLED_RETURNED_TO_COURT]: 'Recalled/Returned to Court',
  [Disposition.SERVED_NOT_YET_RETURNED]: 'Served, Not Yet Returned',
  [Disposition.SERVED_RETURNED_TO_COURT]: 'Served, Returned to Court',
};

export enum InvolvementType {
  PROTECTED_PERSON = 'PROTECTED_PERSON',
  SUBJECT = 'SUBJECT',
  OTHER = 'OTHER',
}

// friendly names for involvement types
export const InvolvementTypeFriendlyNames: Record<InvolvementType, string> = {
  [InvolvementType.PROTECTED_PERSON]: 'Protected Person',
  [InvolvementType.SUBJECT]: 'Subject',
  [InvolvementType.OTHER]: 'Other',
};

export interface IWantedPersonAttempt {
  _id?: string;
  officer: IUser | IUser['_id'];
  date: string;
  notes?: string;
}

export interface IWantedPersonInvolvement {
  masterPerson: IMasterNamePerson | IMasterNamePerson['_id'];
  involvementType: InvolvementType;
}
export interface IWantedPersonDocument extends IAuditModel {
  file: IFile;
  description: string;
}
export enum WantedPersonStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
}
export interface IWantedPerson extends IAuditModel {
  _id?: string;
  processType: ProcessType;
  recordNumber: string;
  docketNumber?: string;
  dateIssued: string;
  wantedFor?: string;
  disposition?: Disposition;
  issuingOfficer?: IUser | IUser['_id'];
  issuingAgency?: IAgency | IAgency['_id'];
  issuingCourt?: IVendor | IVendor['_id'];
  judge?: string;
  bailAmount?: number;
  cashOnly?: boolean;
  nightService?: boolean;
  localId?: string;
  ncicCode?: string;
  offenseCode?: string;
  offenses?: string;
  dateReceived?: string;
  dateExpires?: string;
  dateReturned?: string;
  persons?: IWantedPersonInvolvement[];
  attempts?: IWantedPersonAttempt[];
  documents?: IWantedPersonDocument[];
  crimeClass?: CrimeClass;
  status?: WantedPersonStatus;
  relatedRecords?: {
    recordId: string;
    recordType: string;
  }[];
  relatedCase?: IKase[];
  relatedCfs?: ICFS[];
  relatedTask?: ITask[];
  assignedOfficer?: IUser | IUser['_id'];
  statusDate?: string;
  penalCodes?: (IPenalCode | string)[];
}
